import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import ScrollToTop from './components/util/ScrollToTop';
import Footer from './components/layout/Footer';
import SeoManager from './components/seo/SeoManager';
import { trackMatomoEvent } from './utils/matomo';
import { FaRegCommentDots } from 'react-icons/fa';
import type { IconBaseProps, IconType } from 'react-icons';

import Home from './pages/home/index';
import ServiceDetail from './pages/services/detail';

/**
 * One import function per lazy route, shared with src/index.tsx.
 *
 * index.tsx waits for the current route's module before mounting React on a
 * pre-rendered page. Without that, createRoot wipes the pre-rendered markup the
 * moment main.js runs and Suspense shows its empty fallback until the route
 * chunk arrives - the content appeared at ~0.5s, vanished, and came back at
 * ~2s. Webpack gives both callers the same chunk, so awaiting it here costs no
 * extra request.
 */
export const routeLoaders: Record<string, () => Promise<unknown>> = {
  '/projects': () => import('./pages/projects'),
  '/contact': () => import('./pages/contact'),
  '/imprint': () => import('./pages/terms/imprint'),
  '/privacy-policy': () => import('./pages/terms/privacy-policy'),
  '/cookie-policy': () => import('./pages/terms/cookie-policy'),
  '/services': () => import('./pages/services'),
  '/impact': () => import('./pages/impact'),
  '/about': () => import('./pages/about'),
  '/links': () => import('./pages/instagram-links'),
};

const ProjectsSection = lazy(
  () =>
    routeLoaders['/projects']() as Promise<{ default: React.ComponentType }>,
);
const ContactSection = lazy(
  () => routeLoaders['/contact']() as Promise<{ default: React.ComponentType }>,
);
const Impressum = lazy(
  () => routeLoaders['/imprint']() as Promise<{ default: React.ComponentType }>,
);
const PrivacyPolicy = lazy(
  () =>
    routeLoaders['/privacy-policy']() as Promise<{
      default: React.ComponentType;
    }>,
);
const CookiesPolicyPage = lazy(
  () =>
    routeLoaders['/cookie-policy']() as Promise<{
      default: React.ComponentType;
    }>,
);
const Services = lazy(
  () =>
    routeLoaders['/services']() as Promise<{ default: React.ComponentType }>,
);
const Scene = lazy(
  () => routeLoaders['/impact']() as Promise<{ default: React.ComponentType }>,
);
const About = lazy(
  () => routeLoaders['/about']() as Promise<{ default: React.ComponentType }>,
);
const InstagramLinks = lazy(
  () => routeLoaders['/links']() as Promise<{ default: React.ComponentType }>,
);
const NotFound404 = lazy(() => import('./pages/404'));

const asIconComponent = (Icon: IconType, name: string) => {
  const Comp: React.FC<IconBaseProps> = (props) => {
    const element = Icon(props);
    return React.isValidElement(element) ? element : null;
  };
  Comp.displayName = name;
  return Comp;
};

const MessageIcon = asIconComponent(FaRegCommentDots, 'MessageIcon');

const RouterContent: React.FC = () => {
  const location = useLocation();
  const isInstagramLinks = location.pathname === '/links';

  return (
    <>
      <ScrollToTop />
      <SeoManager />

      {/*
        The footer sits inside the boundary on purpose. Outside it, it painted
        immediately under the 100vh fallback while a lazy route was still
        loading, and then got pushed ~4400px down once the page arrived - a
        0.032 layout shift on every lazy route. Inside, it appears together
        with the content, already in its final position.
      */}
      <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/projects" element={<ProjectsSection />} />
          <Route path="/contact" element={<ContactSection />} />
          <Route path="/imprint" element={<Impressum />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiesPolicyPage />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:serviceId" element={<ServiceDetail />} />
          <Route path="/impact" element={<Scene />} />
          <Route path="/about" element={<About />} />
          <Route path="/links" element={<InstagramLinks />} />
          <Route path="*" element={<NotFound404 />} />
        </Routes>

        {!isInstagramLinks && <Footer />}
      </Suspense>

      {/* Fixed-position, so it never participates in layout shifts. */}
      {!isInstagramLinks && (
        <a
          className="whatsapp-float"
          href="/contact"
          aria-label="WhatsApp"
          onClick={() =>
            trackMatomoEvent('CTA', 'click', 'WhatsApp floating button')
          }
        >
          <MessageIcon />
        </a>
      )}
    </>
  );
};

const Router: React.FC = () => {
  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
};

export default Router;
